import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { diaryKeys } from "@/features/diaries/api/diaryKeys";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import { MissingSideForm } from "@/features/diaries/components/MissingSideForm";
import { PatientSetupPage } from "@/features/diaries/components/PatientSetupPage";
import {
    getPatientFirstSetupStepPath,
    getSharedAttributesForHospitalPrompt,
    isHospitalNumberMissing,
    SKIPPED_HOSPITAL_NUMBER,
} from "@/features/diaries/utils/missingDetailsBuilders";
import { getPostSetupTarget } from "@/features/diaries/utils/postSetupNavigation";
import { useInsertSharedAttributes } from "@/features/shared_attributes/api/insertSharedAttributes";
import { useUpdateSharedAttributes } from "@/features/shared_attributes/api/updateSharedAttributes";
import type { SharedAttributeJSON } from "@/features/shared_attributes/types";
import { hooks as h } from "@/hooks";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { toast } from "@patient-watch/ui/sonner";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { useSetupCopy } from "@/features/i18n/setupCopy";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/utils/auth";

const noopNavigateSearch = (_next?: { i?: number }) => {
    /* single-step route */
};

const noopResetSearch = () => {
    /* single-step route */
};

const SetupHospitalNumberPage = () => {
    const toastCopy = useToastCopy();
    const diaryCopy = useDiaryCopy();
    const setupCopy = useSetupCopy();
    const auth = useAuth();
    const { diaryId } = Route.useParams();
    const queryClient = useQueryClient();
    const navigate = useNavigate({ from: Route.fullPath });
    const router = useRouter();
    const loaderData = Route.useLoaderData() as {
        diary: StatefulDiaryT;
        hospitalSharedAttributes?: SharedAttributeJSON | undefined;
        hospitalSharedAttributesTargets?: SharedAttributeJSON[] | undefined;
    };
    const { hospitalSharedAttributes, hospitalSharedAttributesTargets = [] } = loaderData;

    const navigateAfterHospitalStepSaved = async () => {
        await queryClient.invalidateQueries({ queryKey: diaryKeys.detail(diaryId) });
        const d = await queryClient.fetchQuery(diaryByIdQueryOptions(diaryId));
        const step = d ? getPatientFirstSetupStepPath(d) : null;
        if (step === "setup/hospital-number") {
            void navigate({
                to: "/diaries/$diaryId/setup/hospital-number",
                params: { diaryId },
            });
            return;
        }
        if (step === "setup/reference-date") {
            void navigate({
                to: "/diaries/$diaryId/setup/reference-date",
                params: { diaryId },
            });
            return;
        }
        if (step === "setup/side") {
            void navigate({
                to: "/diaries/$diaryId/setup/side",
                params: { diaryId },
                search: {},
            });
            return;
        }
        const target = await getPostSetupTarget(diaryId, auth.id);
        void navigate(target);
    };
    const { mutateAsync: updateSharedAttributes, isPending: isUpdatePending } = useUpdateSharedAttributes();
    const { mutateAsync: insertSharedAttributes, isPending: isInsertPending } = useInsertSharedAttributes();
    const isSkipPending = isUpdatePending || isInsertPending;

    const handleSkip = async () => {
        try {
            const targets =
                hospitalSharedAttributesTargets.length > 0
                    ? hospitalSharedAttributesTargets
                    : hospitalSharedAttributes
                      ? [hospitalSharedAttributes]
                      : [];

            if (targets.length === 0) {
                await insertSharedAttributes({
                    hospital_number: SKIPPED_HOSPITAL_NUMBER,
                    diary_id: diaryId,
                });
            } else {
                for (const t of targets) {
                    if (t.shared_attribute_id) {
                        await updateSharedAttributes({
                            shared_attribute_id: t.shared_attribute_id,
                            hospital_number: SKIPPED_HOSPITAL_NUMBER,
                            diary_id: diaryId,
                        });
                    } else {
                        await insertSharedAttributes({
                            hospital_number: SKIPPED_HOSPITAL_NUMBER,
                            diary_id: diaryId,
                        });
                    }
                }
            }
            toast.success(toastCopy.savedForNow);
            void navigateAfterHospitalStepSaved();
        } catch {
            toast.error(toastCopy.couldNotSaveHospitalNumber);
        }
    };

    h.useNav([
        { route: { to: "/diaries" }, label: diaryCopy.diaries },
        {
            route: { to: "/diaries/$diaryId/home/to-do", params: { diaryId } },
            label: diaryCopy.diary,
        },
    ]);

    return (
        <PatientSetupPage title={setupCopy.yourHospitalNumber} description={setupCopy.hospitalNumberIntro}>
            <MissingSideForm
                diaryId={diaryId}
                hideFormIntro
                hospitalSection={{
                    sharedAttributes: hospitalSharedAttributes,
                    sharedAttributesTargets: hospitalSharedAttributesTargets,
                    required: false,
                    skippedHospitalNumber: SKIPPED_HOSPITAL_NUMBER,
                }}
                footerBackAction={{ onClick: () => router.history.back(), disabled: isSkipPending }}
                footerSecondaryAction={{
                    label: setupCopy.dontKnow,
                    onClick: () => void handleSkip(),
                    disabled: isSkipPending,
                    isLoading: isSkipPending,
                }}
                items={[]}
                navigateSearch={noopNavigateSearch}
                resetSearch={noopResetSearch}
                onSuccess={() => {
                    void navigateAfterHospitalStepSaved();
                }}
            />
        </PatientSetupPage>
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/setup/hospital-number")({
    component: SetupHospitalNumberPage,
    loader: async ({ context, params }) => {
        const diary = (await context?.queryClient.ensureQueryData(
            diaryByIdQueryOptions(params.diaryId)
        )) as StatefulDiaryT | null;
        if (!diary) {
            throw redirect({ to: "/diaries" });
        }
        const isPatientViewer = (context.auth?.roles?.isPatientOnly ?? false) || context.auth?.id === diary.patient_id;
        if (!isPatientViewer) {
            throw redirect({
                to: "/diaries/$diaryId/home/to-do",
                params: { diaryId: params.diaryId },
            });
        }

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        const hospitalSharedAttributes = getSharedAttributesForHospitalPrompt(diary);

        const targets: SharedAttributeJSON[] = [];
        const seen = new Set<string>();

        const addTarget = (sa: SharedAttributeJSON | undefined) => {
            if (!sa) return;
            if (!isHospitalNumberMissing(sa.hospital_number)) return;
            const id = sa.shared_attribute_id;
            if (id) {
                if (seen.has(id)) return;
                seen.add(id);
            }
            targets.push(sa);
        };

        addTarget(diary.shared_attributes as SharedAttributeJSON | undefined);
        for (const t of diary.trackers_view ?? []) {
            addTarget((t as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes ?? undefined);
        }
        for (const e of diary.events_view ?? []) {
            addTarget((e as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes ?? undefined);
        }

        // When revisiting after the number is set, still bind the existing row so continue can re-save.
        if (targets.length === 0 && hospitalSharedAttributes) {
            targets.push(hospitalSharedAttributes);
        }

        return { diary, hospitalSharedAttributes, hospitalSharedAttributesTargets: targets };
    },
});
